import {
	type VideoDesign,
	VideoDesignSchema,
	type VideoDesignStatusType,
	VideoDesignStatusSchema,
} from "@repo/types";
import { db } from "../client";

export const DBVideoDesignService = {
	async upsertCompiled(data: {
		videoProjectId: string;
		jobId: string;
		organizationId: string;
		design: Record<string, unknown>;
		fps: number;
		durationMs: number;
		width: number;
		height: number;
		status?: VideoDesignStatusType;
	}): Promise<VideoDesign> {
		const status = data.status ?? VideoDesignStatusSchema.Values.COMPILED;
		const compiledAt = new Date();

		const existing = await db.videoDesign.findUnique({
			where: { videoProjectId: data.videoProjectId },
		});

		if (existing) {
			const result = await db.videoDesign.update({
				where: { videoProjectId: data.videoProjectId },
				data: {
					jobId: data.jobId,
					organizationId: data.organizationId,
					status,
					design: data.design,
					fps: data.fps,
					durationMs: data.durationMs,
					width: data.width,
					height: data.height,
					compiledAt,
					latestRenderHistoryId: null,
					latestMp4Url: null,
				},
			});
			return VideoDesignSchema.parse(result);
		}

		const result = await db.videoDesign.create({
			data: {
				videoProjectId: data.videoProjectId,
				jobId: data.jobId,
				organizationId: data.organizationId,
				status,
				design: data.design,
				fps: data.fps,
				durationMs: data.durationMs,
				width: data.width,
				height: data.height,
				compiledAt,
			},
		});
		return VideoDesignSchema.parse(result);
	},

	async getByVideoProjectId(
		videoProjectId: string,
	): Promise<VideoDesign | null> {
		const result = await db.videoDesign.findUnique({
			where: { videoProjectId },
		});
		if (!result) return null;
		return VideoDesignSchema.parse(result);
	},

	async getByLatestRenderHistoryId(
		latestRenderHistoryId: string,
	): Promise<VideoDesign | null> {
		const result = await db.videoDesign.findFirst({
			where: { latestRenderHistoryId },
		});
		if (!result) return null;
		return VideoDesignSchema.parse(result);
	},

	async markRenderQueued(data: {
		videoProjectId: string;
		latestRenderHistoryId: string;
	}): Promise<VideoDesign> {
		const result = await db.videoDesign.update({
			where: { videoProjectId: data.videoProjectId },
			data: {
				latestRenderHistoryId: data.latestRenderHistoryId,
				latestMp4Url: null,
			},
		});
		return VideoDesignSchema.parse(result);
	},

	async markRendered(data: {
		videoProjectId: string;
		latestMp4Url: string;
		latestRenderHistoryId?: string;
	}): Promise<VideoDesign> {
		const result = await db.videoDesign.update({
			where: { videoProjectId: data.videoProjectId },
			data: {
				status: VideoDesignStatusSchema.Values.RENDERED,
				latestMp4Url: data.latestMp4Url,
				...(data.latestRenderHistoryId
					? { latestRenderHistoryId: data.latestRenderHistoryId }
					: {}),
			},
		});
		return VideoDesignSchema.parse(result);
	},

	/** Clear queued render pointer after failure so Overview does not resume polling. */
	async clearLatestRenderHistory(
		videoProjectId: string,
	): Promise<VideoDesign> {
		const result = await db.videoDesign.update({
			where: { videoProjectId },
			data: { latestRenderHistoryId: null },
		});
		return VideoDesignSchema.parse(result);
	},
};
